import React, { useEffect, useState } from "react";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { Label } from "@/modern/components/ui/label";
import { Switch } from "@/modern/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/modern/components/ui/radio-group";
import { Card } from "@/modern/components/ui/card";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { SubcategoryDetailProps } from "../constants/settingsStructure";

export const PersonalScheduleDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project } = useSwivuBryntumContext()
  const { userPreferencesStore: userPreferences } = project;
  const [centerSchedulerLine, setCenterSchedulerLine] = useState<string>("true");
  const [redistributeSubGridPanels, setRedistributeSubGridPanels] = useState(true);
  const [disableTimelineZoomOnMouse, setDisableTimelineZoomOnMouse] = useState(true);
  const [enableRedundantTaskEvents, setEnableRedundantTaskEvents] = useState(false);

  useEffect(() => {
    if (userPreferences) {
      // Load settings
      const centerSetting = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.centerSchedulerLineOnToday, "true");
      // @ts-expect-error - centerSetting is string | undefined
      setCenterSchedulerLine(centerSetting);

      const redistributeSetting = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.redistributeSubGridPanels, "true");
      setRedistributeSubGridPanels(redistributeSetting === "true");

      const disableZoomSetting = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.disableTimelineZoomOnMouse, "true");
      setDisableTimelineZoomOnMouse(disableZoomSetting === "true");

      const redundantEventsSetting = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.enableRedundantTaskEvents, "false");
      setEnableRedundantTaskEvents(redundantEventsSetting === "true");
    }
  }, [userPreferences]);

  const handleCenterSchedulerLineChange = (value: string) => {
    setCenterSchedulerLine(value);
    if (userPreferences) {
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.centerSchedulerLineOnToday, value);
    }
  };

  const handleSettingChange = (key: string, value: boolean) => {
    if (userPreferences) {
      userPreferences.updateSetting(key, String(value));
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <div className="space-y-5 max-w-3xl">
        {/* Timeline Focusing Card */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Timeline Focusing</h3>
              <p className="text-xs text-muted-foreground">
                Configure how the timeline centers and focuses on the current time
              </p>
            </div>

            <div className="space-y-4">
              {/* Radio Group for Timeline Focus */}
              <div className="space-y-3">
                <RadioGroup value={centerSchedulerLine} onValueChange={handleCenterSchedulerLineChange}>
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem value="true" id="centerJustify" className="mt-0.5" />
                    <div className="flex-1 space-y-1">
                      <Label htmlFor="centerJustify" className="text-sm font-medium cursor-pointer">
                        Center justify the current-time line to focus on today
                      </Label>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem value="false" id="leftJustify" className="mt-0.5" />
                    <div className="flex-1 space-y-1">
                      <Label htmlFor="leftJustify" className="text-sm font-medium cursor-pointer">
                        Left justify the current-time line to focus on tomorrow
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Redistribute Panels Setting */}
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="redistributeSubGridPanels" className="text-sm font-medium">
                    When centering or left justifying the timeline, always tidy up the columns
                  </Label>
                </div>
                <Switch
                  id="redistributeSubGridPanels"
                  checked={redistributeSubGridPanels}
                  onCheckedChange={(checked) => {
                    setRedistributeSubGridPanels(checked);
                    handleSettingChange(PERSISTENCE_STATE_KEYS.redistributeSubGridPanels, checked);
                  }}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Additional Personal Settings Card */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Additional Personal Settings</h3>
              <p className="text-xs text-muted-foreground">
                Additional preferences to customize your timeline experience
              </p>
            </div>

            <div className="space-y-4">
              {/* Disable Mouse Wheel Zooming */}
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="disableTimelineZoomOnMouse" className="text-sm font-medium">
                    Disable Mouse Wheel & Trackpad Timeline Zooming
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Prevent accidental zooming when scrolling over the timeline
                  </p>
                </div>
                <Switch
                  id="disableTimelineZoomOnMouse"
                  checked={disableTimelineZoomOnMouse}
                  onCheckedChange={(checked) => {
                    setDisableTimelineZoomOnMouse(checked);
                    handleSettingChange(PERSISTENCE_STATE_KEYS.disableTimelineZoomOnMouse, checked);
                  }}
                />
              </div>

              {/* Enable Redundant Task Events */}
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="enableRedundantTaskEvents" className="text-sm font-medium">
                    Enable redundant task events within time ranges
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Show task events that fall within selected time ranges
                  </p>
                </div>
                <Switch
                  id="enableRedundantTaskEvents"
                  checked={enableRedundantTaskEvents}
                  onCheckedChange={(checked) => {
                    setEnableRedundantTaskEvents(checked);
                    handleSettingChange(PERSISTENCE_STATE_KEYS.enableRedundantTaskEvents, checked);
                  }}
                />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
